/**
 * Main JavaScript file
 *
 * @package Commcore26
 */

import { MobileNav } from './components/mobile-nav';
import { BackToTop } from './components/back-to-top';
import { AmpCarousel } from './components/amp-carousel';
import { TaxonomyFilter } from './components/taxonomy-filter';
import { InView } from './components/inview';

/**
 * Initialize all components when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
	// Initialize mobile navigation
	MobileNav.init();

	// Initialize back to top button
	BackToTop.init();

	// Initialize footer amp carousel
	AmpCarousel.init();

	// Initialize taxonomy filter dropdowns
	TaxonomyFilter.init();

	// Initialize in-view animations
	InView.init();

	// Sticky header: fix header when scrolled past parent org nav
	const header = document.querySelector('.site-header');
	const parentOrgNav = document.getElementById('nav--parent-org');
	const page = document.getElementById('page');

	if (header && parentOrgNav && page) {
		let navHeight = parentOrgNav.offsetHeight;
		let headerHeight = header.offsetHeight;

		const updateHeaderPosition = () => {
			const scrollY = window.scrollY || window.pageYOffset;

			if (scrollY >= navHeight) {
				if (!header.classList.contains('is-fixed')) {
					// Compensate for header leaving document flow
					page.style.paddingTop = headerHeight + 'px';
					header.classList.add('is-fixed');
				}
				header.style.top = `-${navHeight}px`;
			} else {
				if (header.classList.contains('is-fixed')) {
					page.style.paddingTop = '';
					header.classList.remove('is-fixed');
				}
				header.style.top = '';
			}
		};

		const updateNavHeight = () => {
			// Temporarily remove fixed positioning to measure natural heights
			const wasFixed = header.classList.contains('is-fixed');
			if (wasFixed) {
				header.classList.remove('is-fixed');
				page.style.paddingTop = '';
			}
			navHeight = parentOrgNav.offsetHeight;
			headerHeight = header.offsetHeight;
			if (wasFixed) {
				header.classList.add('is-fixed');
				page.style.paddingTop = headerHeight + 'px';
			}
			updateHeaderPosition();
		};

		window.addEventListener('scroll', updateHeaderPosition, { passive: true });
		window.addEventListener('resize', updateNavHeight, { passive: true });
		updateHeaderPosition();
	}

	// Dispatch custom event when theme is initialized
	document.dispatchEvent(new CustomEvent('commcore26:init'));
});

/**
 * Global utilities
 */
window.commcore26 = window.commcore26 || {};

/**
 * Debounce function
 *
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function}
 */
window.commcore26.debounce = function(func, wait = 100) {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

/**
 * Throttle function
 *
 * @param {Function} func - Function to throttle
 * @param {number} limit - Limit time in milliseconds
 * @returns {Function}
 */
window.commcore26.throttle = function(func, limit = 100) {
	let inThrottle;
	return function executedFunction(...args) {
		if (!inThrottle) {
			func(...args);
			inThrottle = true;
			setTimeout(() => inThrottle = false, limit);
		}
	};
};

/**
 * Check if element is in viewport
 *
 * @param {HTMLElement} element - Element to check
 * @param {number} offset - Offset from viewport edge
 * @returns {boolean}
 */
window.commcore26.isInViewport = function(element, offset = 0) {
	const rect = element.getBoundingClientRect();
	return (
		rect.top >= -offset &&
		rect.left >= -offset &&
		rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + offset &&
		rect.right <= (window.innerWidth || document.documentElement.clientWidth) + offset
	);
};
